import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import OpacityVariable from '@arcgis/core/renderers/visualVariables/OpacityVariable';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import React, { useContext, useEffect, useMemo } from 'react'
import observationFields from './observationFields';
import { MapContext } from '../../../../App/MapContextContainer';
import { ObservationsContext } from '../../ObservationsContextContainer';
import { concatenateStrings } from './concatenateStrings';
import { cuttingRealizationPracticesEsriSQLReducer, getFromAndToDatesFromMonths } from '../../../utils';
import { AppSettingsContext } from '../../../../App/AppSettingsContextContainer';

const fillSymbol = new SimpleFillSymbol({
    color: "red",
    style: 'solid',
    outline: {
        color: "red",
        width: 2
    }
});

const clashingSymbol = new SimpleMarkerSymbol({
    color: "red",
    size: "8"
});

const polygonsRenderer = new SimpleRenderer({
    symbol: fillSymbol,
    visualVariables: [new OpacityVariable({
        field: "isRadius",
        stops: [
            { value: 0, opacity: 1 },
            { value: 1, opacity: 0.1 }],
        legendOptions: {
            showLegend: false
        }
    })]
});

const pointsRenderer = new SimpleRenderer({
    symbol: clashingSymbol
});

const MIN_SCALE_TO_SHOW_CLASHES = 300000;

export default function useObservationClashLayers() {
    const mapContext = useContext(MapContext);
    const observationsContext = useContext(ObservationsContext);
    const { appliedFilters } = useContext(AppSettingsContext);

    const pointsObservationsFeatureLayer = useMemo(() => new FeatureLayer({
        source: [],
        renderer: pointsRenderer,
        title: "Metsänkäyttöilmoitukset lajihavainnoissa",
        labelsVisible: false,
        minScale: MIN_SCALE_TO_SHOW_CLASHES,
        outFields: ["*"],
        objectIdField: "fakeoid",
        legendEnabled: false,
        popupEnabled: true,
        spatialReference: { wkid: 4326 },
        geometryType: "point",
        fields: observationFields
    }), []);

    const polygonsObservationsFeatureLayer = useMemo(() => new FeatureLayer({
        source: [],
        renderer: polygonsRenderer,
        title: "Metsänkäyttöilmoitukset lajihavainnoissa (aluetyyppiset)",
        labelsVisible: false,
        minScale: MIN_SCALE_TO_SHOW_CLASHES,
        outFields: ["*"],
        objectIdField: "fakeoid",
        legendEnabled: false,
        popupEnabled: true,
        spatialReference: { wkid: 4326 },
        geometryType: "polygon",
        fields: observationFields
    }), []);

    useEffect(() => {
        if (mapContext.mapAndViewReady === false || observationsContext.observationsState === undefined) {
            return;
        }

        let definitionExpression = "";

        if (observationsContext.observationsState.collectionIds.length > 0) {
            definitionExpression = `document.collectionId IN(${concatenateStrings(observationsContext.observationsState.collectionIds)
                })`;
        }

        if (observationsContext.observationsState.taxonIds.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            definitionExpression = definitionExpression + `unit.linkings.taxon.id IN(${concatenateStrings(observationsContext.observationsState.taxonIds)
                })`;
        }

        if (observationsContext.observationsState.observationMonths.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            const { fromDate, toDate } = getFromAndToDatesFromMonths(observationsContext.observationsState.observationMonths);

            if (fromDate && toDate) {
                definitionExpression = definitionExpression + `gathering.eventDate.begin >= '${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01' AND `;
                definitionExpression = definitionExpression + `gathering.eventDate.end <= '${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}'`;
            }
        }

        if (observationsContext.observationsState.declarationArrivalMonths.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            const { fromDate, toDate } = getFromAndToDatesFromMonths(observationsContext.observationsState.declarationArrivalMonths);

            if (fromDate && toDate) {
                definitionExpression = definitionExpression + `declarationArrivalDate >= '${fromDate.getFullYear()}-${(fromDate.getMonth() + 1 + "").padStart(2, "0")}-01' AND `;
                definitionExpression = definitionExpression + `declarationArrivalDate <= '${toDate.getFullYear()}-${(toDate.getMonth() + 1 + "").padStart(2, "0")}-${toDate.getDate()}'`;
            }
        }

        if (observationsContext.observationsState.cuttingRealizationPractices.length > 0) {
            if (definitionExpression !== "") {
                definitionExpression += " AND ";
            }

            const cuttingRealizationPracticeSQL = observationsContext.observationsState.cuttingRealizationPractices.reduce(cuttingRealizationPracticesEsriSQLReducer, "");

            definitionExpression = definitionExpression + "(" + cuttingRealizationPracticeSQL + ")";
        }

        pointsObservationsFeatureLayer.definitionExpression = definitionExpression;
        polygonsObservationsFeatureLayer.definitionExpression = definitionExpression;
    }, [mapContext.mapAndViewReady, observationsContext.observationsState, appliedFilters]);

    return {
        pointsObservationsFeatureLayer,
        polygonsObservationsFeatureLayer
    }
}